// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { CoinHeaderCell, StandardHeaderCell, getTableHeaderCellClasses, getStickyPositionStyle } from './TableHeaderCells';
import { ColumnTooltip } from './ColumnTooltip';
import { BarChart3 } from 'lucide-react';
import CoinLogoImage from './CoinLogoImage';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface TechnicalIndicator {
  coin_id: string;
  symbol: string;
  name: string;
  logo_url?: string;
  current_price: number;
  rsi_value: number;
  rsi_status: string;
  macd_line: number;
  macd_signal: number;
  macd_histogram: number;
  macd_signal_status: string;
  bb_upper: number;
  bb_lower: number;
  bb_distance_upper_pct: number;
  bb_distance_lower_pct: number;
  obv_value: number;
  obv_signal: string;
  obv_7d_change_pct: number;
  obv_30d_change_pct: number;
  sma_50_day: number;
  sma_50_day_position_pct: number;
  sma_50_day_slope: number;
  sma_50_day_trend: string;
  sma_150_day: number;
  sma_150_day_position_pct: number;
  sma_150_day_slope: number;
  sma_150_day_trend: string;
  sma_200_day: number;
  sma_200_day_position_pct: number;
  sma_200_day_slope: number;
  sma_200_day_trend: string;
  sma_50_week: number;
  sma_50_week_position_pct: number;
  sma_50_week_slope: number;
  sma_50_week_trend: string;
  sma_200_week: number;
  sma_200_week_position_pct: number;
  sma_200_week_slope: number;
  sma_200_week_trend: string;
  data_date: string;
}

interface TechnicalIndicatorsData {
  summary: {
    total_coins: number;
    timestamp: string;
    data_freshness: string;
  };
  indicators: TechnicalIndicator[];
  timestamp: string;
}

const TechnicalIndicators: React.FC = () => {
  const { isDark } = useTheme();
  const [data, setData] = useState<TechnicalIndicatorsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<{[key: string]: boolean}>(() => {
    const saved = localStorage.getItem('technicalIndicatorsColumns');
    return saved ? JSON.parse(saved) : {
      rank: true,
      coin: true,
      price: true,
      rsi: true,
      macd: true,
      bb: true,
      obv: true,
      sma50d: true,
      sma150d: true,
      sma200d: true,
      sma50w: true,
      sma200w: true,
    };
  });

  // Pinned columns state
  const [pinnedColumns, setPinnedColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem('technicalIndicatorsPinnedColumns');
    return saved ? JSON.parse(saved) : [];
  });

  const [showPinLimitToast, setShowPinLimitToast] = useState(false);

  const fetchTechnicalIndicators = async () => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) {
      setError('Authentication required. Please log in to view technical indicators.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/technical-indicators?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      setData(result);

    } catch (err: any) {
      console.error('Technical Indicators API error:', err);
      setError('Failed to load technical indicators. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTechnicalIndicators();
  }, []);

  // Add horizontal scroll on Shift+wheel or trackpad horizontal swipe
  useEffect(() => {
    const scrollContainer = tableScrollRef.current;
    if (!scrollContainer) return;

    const handleWheel = (e: WheelEvent) => {
      const hasHorizontalScroll = scrollContainer.scrollWidth > scrollContainer.clientWidth;

      // Only hijack scroll if:
      // 1. Table has horizontal overflow AND
      // 2. User is holding Shift OR doing horizontal trackpad gesture (deltaX !== 0)
      if (hasHorizontalScroll && (e.shiftKey || Math.abs(e.deltaX) > 0)) {
        e.preventDefault();
        e.stopPropagation();
        // Use deltaX for trackpad horizontal swipe, or deltaY when Shift is held
        const scrollAmount = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        scrollContainer.scrollLeft += scrollAmount;
      }
      // Otherwise, let the page scroll vertically normally
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, [data]);

  const toggleColumn = (columnKey: string) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey]
    };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('technicalIndicatorsColumns', JSON.stringify(newVisibleColumns));
  };

  const togglePin = (columnKey: string) => {
    const isPinned = pinnedColumns.includes(columnKey);

    if (isPinned) {
      const filteredColumns = pinnedColumns.filter(col => col !== columnKey);
      const newPinnedColumns = sortPinnedColumns(filteredColumns);
      setPinnedColumns(newPinnedColumns);
      localStorage.setItem('technicalIndicatorsPinnedColumns', JSON.stringify(newPinnedColumns));
    } else {
      if (pinnedColumns.length >= 6) {
        setShowPinLimitToast(true);
        setTimeout(() => setShowPinLimitToast(false), 3000);
        return;
      }

      const unsortedColumns = [...pinnedColumns, columnKey];
      const newPinnedColumns = sortPinnedColumns(unsortedColumns);
      setPinnedColumns(newPinnedColumns);
      localStorage.setItem('technicalIndicatorsPinnedColumns', JSON.stringify(newPinnedColumns));
    }
  };

  const PIN_ORDER = [
    'rsi',
    'macd',
    'bb',
    'obv',
    'sma50d',
    'sma150d',
    'sma200d',
    'sma50w',
    'sma200w',
  ];

  const sortPinnedColumns = (columns: string[]): string[] => {
    return PIN_ORDER.filter(col => columns.includes(col));
  };

  const getColumnLeftPosition = (columnKey: string): number => {
    const columnWidths: {[key: string]: number} = {
      rank: 80,
      coin: 180,
      price: 120,
      rsi: 140,
      macd: 150,
      bb: 160,
      obv: 200,
      sma50d: 140,
      sma150d: 140,
      sma200d: 140,
      sma50w: 140,
      sma200w: 140,
    };

    const orderedPinnedColumns = sortPinnedColumns(pinnedColumns);
    const columnIndex = orderedPinnedColumns.indexOf(columnKey);
    if (columnIndex === -1) return 0;

    let leftPosition = (columnWidths.rank || 0) + (columnWidths.coin || 0) + (columnWidths.price || 0);

    for (let i = 0; i < columnIndex; i++) {
      const prevColumn = orderedPinnedColumns[i];
      leftPosition += columnWidths[prevColumn] || 100;
    }
    return leftPosition;
  };

  const formatPrice = (value: number) => {
    if (!value) return '-';
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value: number) => {
    if (value === null || value === undefined) return '-';
    const color = value >= 0
      ? (isDark ? 'text-emerald-400' : 'text-green-600')
      : (isDark ? 'text-red-400' : 'text-red-600');
    const sign = value >= 0 ? '+' : '';
    return (
      <span className={color}>{sign}{value.toFixed(2)}%</span>
    );
  };

  const getRSIBadge = (rsi: number, status: string) => {
    if (!rsi) return <span className="text-gray-400">-</span>;

    let badgeClass = '';
    if (isDark) {
      if (status === 'Overbought') badgeClass = 'bg-red-400/20 text-red-400 border-red-400/30';
      else if (status === 'Oversold') badgeClass = 'bg-green-400/20 text-green-400 border-green-400/30';
      else badgeClass = 'bg-gray-400/20 text-gray-400 border-gray-400/30';
    } else {
      if (status === 'Overbought') badgeClass = 'bg-red-100 text-red-800 border-red-200';
      else if (status === 'Oversold') badgeClass = 'bg-green-100 text-green-800 border-green-200';
      else badgeClass = 'bg-gray-100 text-gray-800 border-gray-200';
    }

    return (
      <div className="flex flex-col items-center gap-1">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badgeClass}`}>
          {rsi.toFixed(1)}
        </span>
        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{status}</span>
      </div>
    );
  };

  const getMACDBadge = (macdStatus: string) => {
    if (!macdStatus || macdStatus === 'N/A') return <span className="text-gray-400">-</span>;

    const isBullish = macdStatus === 'Bullish';
    const badgeClass = isBullish
      ? (isDark ? 'bg-green-400/20 text-green-400 border-green-400/30' : 'bg-green-100 text-green-800 border-green-200')
      : (isDark ? 'bg-red-400/20 text-red-400 border-red-400/30' : 'bg-red-100 text-red-800 border-red-200');

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badgeClass}`}>
        {isBullish ? '🟢' : '🔴'} {macdStatus}
      </span>
    );
  };

  const getTrendBadge = (trend: string) => {
    if (!trend || trend === 'N/A') return <span className="text-gray-400">-</span>;

    let badgeClass = '';
    let icon = '';

    if (isDark) {
      if (trend === 'Upward') {
        badgeClass = 'bg-green-400/20 text-green-400 border-green-400/30';
        icon = '↗';
      } else if (trend === 'Downward') {
        badgeClass = 'bg-red-400/20 text-red-400 border-red-400/30';
        icon = '↘';
      } else {
        badgeClass = 'bg-gray-400/20 text-gray-400 border-gray-400/30';
        icon = '→';
      }
    } else {
      if (trend === 'Upward') {
        badgeClass = 'bg-green-100 text-green-800 border-green-200';
        icon = '↗';
      } else if (trend === 'Downward') {
        badgeClass = 'bg-red-100 text-red-800 border-red-200';
        icon = '↘';
      } else {
        badgeClass = 'bg-gray-100 text-gray-800 border-gray-200';
        icon = '→';
      }
    }

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${badgeClass}`}>
        {icon} {trend}
      </span>
    );
  };

  const getOBVBadge = (obvSignal: string, obv7d: number, obv30d: number) => {
    if (!obvSignal || obvSignal === 'N/A') return <span className="text-gray-400">-</span>;

    let badgeClass = '';
    let icon = '';
    let displayText = obvSignal;

    if (isDark) {
      if (obvSignal === 'Bullish' || obvSignal === 'Bullish Divergence') {
        badgeClass = 'bg-green-400/20 text-green-400 border-green-400/30';
        icon = obvSignal === 'Bullish Divergence' ? '🚀' : '🟢';
      } else {
        badgeClass = 'bg-red-400/20 text-red-400 border-red-400/30';
        icon = obvSignal === 'Bearish Divergence' ? '⚠️' : '🔴';
      }
    } else {
      if (obvSignal === 'Bullish' || obvSignal === 'Bullish Divergence') {
        badgeClass = 'bg-green-100 text-green-800 border-green-200';
        icon = obvSignal === 'Bullish Divergence' ? '🚀' : '🟢';
      } else {
        badgeClass = 'bg-red-100 text-red-800 border-red-200';
        icon = obvSignal === 'Bearish Divergence' ? '⚠️' : '🔴';
      }
    }

    return (
      <div className="flex flex-col items-center gap-1">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badgeClass}`}>
          {icon} {displayText}
        </span>
        <div className="flex gap-2 text-xs">
          <span className={`${obv7d >= 0 ? (isDark ? 'text-emerald-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}`}>
            7d: {obv7d >= 0 ? '+' : ''}{obv7d?.toFixed(1)}%
          </span>
          <span className={`${obv30d >= 0 ? (isDark ? 'text-emerald-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}`}>
            30d: {obv30d >= 0 ? '+' : ''}{obv30d?.toFixed(1)}%
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={`rounded-2xl shadow-sm border mb-6 transition-all duration-200 ${
      isDark
        ? 'bg-white/[0.03] border-white/10'
        : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${
        isDark
          ? 'border-white/10'
          : 'border-gray-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
              <BarChart3 className={`${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} size={24} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Technical Indicators Overview
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                RSI, MACD, Bollinger Bands, OBV, and SMA positions for all coins
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div>
        {/* Loading State */}
        {loading && (
          <div className="px-6">
            <div className="flex items-center justify-center py-12">
              <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDark ? 'border-emerald-400' : 'border-blue-600'}`}></div>
              <span className={`ml-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading technical indicators...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="px-6">
            <div className={`border rounded-lg p-4 ${
              isDark
                ? 'bg-red-500/10 border-red-400/20'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center space-x-2">
                <span className={isDark ? 'text-red-400' : 'text-red-600'}>❌</span>
                <span className={`font-medium ${isDark ? 'text-red-400' : 'text-red-800'}`}>Error loading data:</span>
                <span className={isDark ? 'text-red-300' : 'text-red-700'}>{error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        {data?.indicators && data.indicators.length > 0 && (
          <div
            ref={tableScrollRef}
            className={`overflow-x-auto custom-scrollbar ${!isDark ? 'light-scrollbar' : ''} relative`}
            title="Hold Shift + scroll to move horizontally"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: isDark ? '#4b5563 #1f2937' : '#d1d5db #f3f4f6'
            }}
          >
            <table className={`w-full text-sm ${isDark ? '' : ''}`}>
              <thead className={`text-xs uppercase tracking-wide ${isDark ? 'opacity-80' : 'opacity-80'}`}>
                <tr className={`h-24 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                  <th
                    className={`h-24 text-center align-top sticky left-0 z-10 bg-inherit`}
                    style={{ width: '80px', minWidth: '80px', position: 'sticky', left: 0, top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }}
                  >
                    <CoinHeaderCell title="Rank" />
                  </th>
                  <th
                    className={`h-24 text-center align-top sticky z-10 bg-inherit`}
                    style={{ width: '180px', minWidth: '180px', position: 'sticky', left: '80px', top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }}
                  >
                    <CoinHeaderCell title="Coin" />
                  </th>
                  <th
                    className={`h-24 text-center align-top sticky z-10 bg-inherit`}
                    style={{ width: '120px', minWidth: '120px', position: 'sticky', left: '260px', top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }}
                  >
                    <CoinHeaderCell title="Price" />
                  </th>
                  <th
                    className={getTableHeaderCellClasses(visibleColumns.rsi, pinnedColumns.includes('rsi'), isDark)}
                    style={getStickyPositionStyle(pinnedColumns.includes('rsi'), getColumnLeftPosition('rsi'), isDark)}
                  >
                    <StandardHeaderCell
                      colKey="rsi"
                      label="RSI (14)"
                      abbrev="RSI"
                      visible={visibleColumns.rsi}
                      pinned={pinnedColumns.includes('rsi')}
                      onToggleColumn={toggleColumn}
                      onTogglePin={togglePin}
                    />
                  </th>
                  <th
                    className={getTableHeaderCellClasses(visibleColumns.macd, pinnedColumns.includes('macd'), isDark)}
                    style={getStickyPositionStyle(pinnedColumns.includes('macd'), getColumnLeftPosition('macd'), isDark)}
                  >
                    <StandardHeaderCell
                      colKey="macd"
                      label="MACD Signal"
                      abbrev="MACD"
                      visible={visibleColumns.macd}
                      pinned={pinnedColumns.includes('macd')}
                      onToggleColumn={toggleColumn}
                      onTogglePin={togglePin}
                    />
                  </th>
                  <th
                    className={getTableHeaderCellClasses(visibleColumns.bb, pinnedColumns.includes('bb'), isDark)}
                    style={getStickyPositionStyle(pinnedColumns.includes('bb'), getColumnLeftPosition('bb'), isDark)}
                  >
                    <StandardHeaderCell
                      colKey="bb"
                      label="Bollinger Bands"
                      abbrev="BB"
                      visible={visibleColumns.bb}
                      pinned={pinnedColumns.includes('bb')}
                      onToggleColumn={toggleColumn}
                      onTogglePin={togglePin}
                    />
                  </th>
                  <th
                    className={getTableHeaderCellClasses(visibleColumns.obv, pinnedColumns.includes('obv'), isDark)}
                    style={getStickyPositionStyle(pinnedColumns.includes('obv'), getColumnLeftPosition('obv'), isDark)}
                  >
                    <StandardHeaderCell
                      colKey="obv"
                      label="OBV (On-Balance Volume)"
                      abbrev="OBV"
                      visible={visibleColumns.obv}
                      pinned={pinnedColumns.includes('obv')}
                      onToggleColumn={toggleColumn}
                      onTogglePin={togglePin}
                    />
                  </th>
                  <th
                    className={getTableHeaderCellClasses(visibleColumns.sma50d, pinnedColumns.includes('sma50d'), isDark)}
                    style={getStickyPositionStyle(pinnedColumns.includes('sma50d'), getColumnLeftPosition('sma50d'), isDark)}
                  >
                    <StandardHeaderCell
                      colKey="sma50d"
                      label="50-Day SMA"
                      abbrev="50D"
                      visible={visibleColumns.sma50d}
                      pinned={pinnedColumns.includes('sma50d')}
                      onToggleColumn={toggleColumn}
                      onTogglePin={togglePin}
                    />
                  </th>
                  <th
                    className={getTableHeaderCellClasses(visibleColumns.sma150d, pinnedColumns.includes('sma150d'), isDark)}
                    style={getStickyPositionStyle(pinnedColumns.includes('sma150d'), getColumnLeftPosition('sma150d'), isDark)}
                  >
                    <StandardHeaderCell
                      colKey="sma150d"
                      label="150-Day SMA"
                      abbrev="150D"
                      visible={visibleColumns.sma150d}
                      pinned={pinnedColumns.includes('sma150d')}
                      onToggleColumn={toggleColumn}
                      onTogglePin={togglePin}
                    />
                  </th>
                  <th
                    className={getTableHeaderCellClasses(visibleColumns.sma200d, pinnedColumns.includes('sma200d'), isDark)}
                    style={getStickyPositionStyle(pinnedColumns.includes('sma200d'), getColumnLeftPosition('sma200d'), isDark)}
                  >
                    <StandardHeaderCell
                      colKey="sma200d"
                      label="200-Day SMA"
                      abbrev="200D"
                      visible={visibleColumns.sma200d}
                      pinned={pinnedColumns.includes('sma200d')}
                      onToggleColumn={toggleColumn}
                      onTogglePin={togglePin}
                    />
                  </th>
                  <th
                    className={getTableHeaderCellClasses(visibleColumns.sma50w, pinnedColumns.includes('sma50w'), isDark)}
                    style={getStickyPositionStyle(pinnedColumns.includes('sma50w'), getColumnLeftPosition('sma50w'), isDark)}
                  >
                    <StandardHeaderCell
                      colKey="sma50w"
                      label="50-Week SMA"
                      abbrev="50W"
                      visible={visibleColumns.sma50w}
                      pinned={pinnedColumns.includes('sma50w')}
                      onToggleColumn={toggleColumn}
                      onTogglePin={togglePin}
                    />
                  </th>
                  <th
                    className={getTableHeaderCellClasses(visibleColumns.sma200w, pinnedColumns.includes('sma200w'), isDark)}
                    style={getStickyPositionStyle(pinnedColumns.includes('sma200w'), getColumnLeftPosition('sma200w'), isDark)}
                  >
                    <StandardHeaderCell
                      colKey="sma200w"
                      label="200-Week SMA"
                      abbrev="200W"
                      visible={visibleColumns.sma200w}
                      pinned={pinnedColumns.includes('sma200w')}
                      onToggleColumn={toggleColumn}
                      onTogglePin={togglePin}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.indicators.map((indicator, index) => (
                  <tr key={`${indicator.symbol}-${index}`} className={`${isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-50'}`}>
                    <td
                      className={`px-4 py-4 whitespace-nowrap text-sm font-medium sticky left-0 z-10 ${isDark ? 'text-white' : 'text-gray-900'}`}
                      style={{ width: '80px', minWidth: '80px', position: 'sticky', left: 0, top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }}
                    >
                      #{index + 1}
                    </td>
                    <td
                      className={`px-4 py-4 whitespace-nowrap sticky z-10 bg-inherit`}
                      style={{ width: '180px', minWidth: '180px', position: 'sticky', left: '80px', top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }}
                    >
                      <div className="flex items-center">
                        <CoinLogoImage
                          src={indicator.logo_url || ''}
                          alt={indicator.symbol}
                          size="md"
                          className="mr-3"
                        />
                        <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{indicator.symbol}</div>
                      </div>
                    </td>
                    <td
                      className={`px-4 py-4 whitespace-nowrap text-sm font-medium sticky z-10 ${isDark ? 'text-white' : 'text-gray-900'}`}
                      style={{ width: '120px', minWidth: '120px', position: 'sticky', left: '260px', top: 0, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' }}
                    >
                      {formatPrice(indicator.current_price)}
                    </td>
                    <td className={`py-2 px-4 text-center ${pinnedColumns.includes('rsi') ? 'sticky' : ''}`}
                      style={pinnedColumns.includes('rsi') ? { position: 'sticky', left: `${getColumnLeftPosition('rsi')}px`, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' } : undefined}>
                      {getRSIBadge(indicator.rsi_value, indicator.rsi_status)}
                    </td>
                    <td className={`py-2 px-4 text-center ${pinnedColumns.includes('macd') ? 'sticky' : ''}`}
                      style={pinnedColumns.includes('macd') ? { position: 'sticky', left: `${getColumnLeftPosition('macd')}px`, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' } : undefined}>
                      {getMACDBadge(indicator.macd_signal_status)}
                    </td>
                    <td className={`py-2 px-4 text-center ${pinnedColumns.includes('bb') ? 'sticky' : ''}`}
                      style={pinnedColumns.includes('bb') ? { position: 'sticky', left: `${getColumnLeftPosition('bb')}px`, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' } : undefined}>
                      <div className="flex flex-col gap-1">
                        <div className="text-xs">Upper: {formatPercentage(indicator.bb_distance_upper_pct)}</div>
                        <div className="text-xs">Lower: {formatPercentage(indicator.bb_distance_lower_pct)}</div>
                      </div>
                    </td>
                    <td className={`py-2 px-4 text-center ${pinnedColumns.includes('obv') ? 'sticky' : ''}`}
                      style={pinnedColumns.includes('obv') ? { position: 'sticky', left: `${getColumnLeftPosition('obv')}px`, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' } : undefined}>
                      {getOBVBadge(indicator.obv_signal, indicator.obv_7d_change_pct, indicator.obv_30d_change_pct)}
                    </td>
                    <td className={`py-2 px-4 text-center ${pinnedColumns.includes('sma50d') ? 'sticky' : ''}`}
                      style={pinnedColumns.includes('sma50d') ? { position: 'sticky', left: `${getColumnLeftPosition('sma50d')}px`, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' } : undefined}>
                      <div className="flex flex-col gap-1 items-center">
                        <div>{formatPercentage(indicator.sma_50_day_position_pct)}</div>
                        <div>{getTrendBadge(indicator.sma_50_day_trend)}</div>
                      </div>
                    </td>
                    <td className={`py-2 px-4 text-center ${pinnedColumns.includes('sma150d') ? 'sticky' : ''}`}
                      style={pinnedColumns.includes('sma150d') ? { position: 'sticky', left: `${getColumnLeftPosition('sma150d')}px`, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' } : undefined}>
                      <div className="flex flex-col gap-1 items-center">
                        <div>{formatPercentage(indicator.sma_150_day_position_pct)}</div>
                        <div>{getTrendBadge(indicator.sma_150_day_trend)}</div>
                      </div>
                    </td>
                    <td className={`py-2 px-4 text-center ${pinnedColumns.includes('sma200d') ? 'sticky' : ''}`}
                      style={pinnedColumns.includes('sma200d') ? { position: 'sticky', left: `${getColumnLeftPosition('sma200d')}px`, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' } : undefined}>
                      <div className="flex flex-col gap-1 items-center">
                        <div>{formatPercentage(indicator.sma_200_day_position_pct)}</div>
                        <div>{getTrendBadge(indicator.sma_200_day_trend)}</div>
                      </div>
                    </td>
                    <td className={`py-2 px-4 text-center ${pinnedColumns.includes('sma50w') ? 'sticky' : ''}`}
                      style={pinnedColumns.includes('sma50w') ? { position: 'sticky', left: `${getColumnLeftPosition('sma50w')}px`, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' } : undefined}>
                      <div className="flex flex-col gap-1 items-center">
                        <div>{formatPercentage(indicator.sma_50_week_position_pct)}</div>
                        <div>{getTrendBadge(indicator.sma_50_week_trend)}</div>
                      </div>
                    </td>
                    <td className={`py-2 px-4 text-center ${pinnedColumns.includes('sma200w') ? 'sticky' : ''}`}
                      style={pinnedColumns.includes('sma200w') ? { position: 'sticky', left: `${getColumnLeftPosition('sma200w')}px`, zIndex: 2, background: isDark ? '#0a0a0a' : '#ffffff' } : undefined}>
                      <div className="flex flex-col gap-1 items-center">
                        <div>{formatPercentage(indicator.sma_200_week_position_pct)}</div>
                        <div>{getTrendBadge(indicator.sma_200_week_trend)}</div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* No Data State */}
        {data?.indicators && data.indicators.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-5xl text-gray-400 mb-4">📊</div>
            <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>No technical indicators found</h3>
            <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Data is being calculated. Please check back later.
            </p>
          </div>
        )}
      </div>

      {/* Toast notification for pin limit */}
      {showPinLimitToast && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 ${
            isDark
              ? 'bg-red-500/90 text-white border border-red-400'
              : 'bg-red-100 text-red-900 border border-red-300'
          }`}
          role="alert"
        >
          Limit reached (6). Unpin another column to continue.
        </div>
      )}
    </div>
  );
};

export default TechnicalIndicators;
